<script lang="ts" setup>
import { ElOption, ElSelect } from "element-plus";
import { computed, inject, nextTick, ref, watch, watchEffect } from "vue";
import { DICT_STORE, excludeObjectKeys } from "../../../utils";
import { SelectEmits, SelectProps } from "./select";

defineOptions({
  name: "ISelect",
});
const props = defineProps(SelectProps);
const emits = defineEmits(SelectEmits);
const dictStore = inject(DICT_STORE);

const bindAttrs = computed(() => {
  return excludeObjectKeys(props, [
    "data",
    "dataCallback",
    "optLabel",
    "optValue",
    "modelValue",
    "action",
  ]);
});

const _loading = ref(false);
const optionList = ref<any[]>([]);
const selectRef = ref();
const selectValue = ref(props.modelValue);

watchEffect(() => {
  _loading.value = props.loading;
});
watch(
  () => props.data,
  () => setOptions(),
  { deep: true, immediate: true },
);
watch(
  () => props.modelValue,
  (val: any) => (selectValue.value = val),
  { deep: true, immediate: true },
);

defineExpose({
  getRef() {
    return selectRef.value;
  },
  focus() {
    selectRef.value?.focus();
  },
  blur() {
    selectRef.value?.blur();
  },
});

const handleChange = (value: any) => {
  // ElSelect 单选清空时值为 undefined，统一转为空字符串
  const normalized = props.multiple ? value : value === undefined ? "" : value;
  emits("update:modelValue", normalized);
  emits("change", normalized);
};

async function setOptions() {
  _loading.value = true;
  const list = await getList();
  optionList.value = props.dataCallback ? props.dataCallback(list) : list;
  await nextTick();
  _loading.value = false;
}

async function getList() {
  if (Array.isArray(props.data)) {
    return props.data;
  } else {
    if (props?.action) return await props.action?.getDict(props.data);
    if (dictStore) return await dictStore.getDict(props.data);
    return [];
  }
}
function handleVisibleChange(visible: boolean) {
  emits("visibleChange", visible);
}
function handleRemoveTag(tagValue: any) {
  emits("removeTag", tagValue);
}
</script>

<template>
  <ElSelect
    ref="selectRef"
    v-model="selectValue"
    class="i-select"
    v-bind="bindAttrs"
    :loading="_loading"
    @change="handleChange"
    @visible-change="handleVisibleChange"
    @remove-tag="handleRemoveTag"
    @clear="emits('clear')"
    @blur="(event) => emits('blur', event)"
    @focus="(event) => emits('focus', event)"
  >
    <ElOption
      v-for="(option, index) of optionList"
      :key="option[optValue]"
      :label="option[optLabel]"
      :value="option[optValue]"
      :disabled="option?.disabled"
    >
      <template #default>
        <slot name="default" :data="option" :index="index" />
      </template>
    </ElOption>
  </ElSelect>
</template>
